import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Toast, PullToRefresh, FloatingBubble, Picker } from 'antd-mobile';
import { AddOutline, DownOutline } from 'antd-mobile-icons';
import { productService } from '../services/productService';
import { brandService } from '../services/brandService';
import { Product, Brand } from '../types';
import SearchHeader from '../components/SearchHeader';
import BottomNav from '../components/BottomNav';
import EmptyState from '../components/EmptyState';
import ProductSkeleton from '../components/ProductSkeleton';
import LazyImage from '../components/LazyImage';
import './ProductList.css';

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedColors, setSelectedColors] = useState<Record<string, number>>({}); // 记录每个商品选中的颜色索引
  const [colorPickerVisible, setColorPickerVisible] = useState<string | null>(null); // 当前打开的颜色选择器
  const navigate = useNavigate();

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getProducts();
      setProducts(data);
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: '加载商品失败',
      });
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await brandService.getActiveBrands();
      setBrands(data);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  useEffect(() => {
    loadProducts();
    loadBrands();
  }, []);

  const handleProductClick = useCallback((id: string) => {
    navigate(`/products/${id}/edit`);
  }, [navigate]);

  const handleProductDetailClick = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    navigate(`/products/${id}`);
  }, [navigate]);

  const handleAddProduct = useCallback(() => {
    navigate('/products/new');
  }, [navigate]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // 处理颜色选择
  const handleColorSelect = useCallback((productId: string, value: (string | number | null)[]) => {
    if (!value || value.length === 0 || value[0] === null) return;
    const colorIndex = value[0] as number;
    setSelectedColors(prev => ({
      ...prev,
      [productId]: colorIndex
    }));
    setColorPickerVisible(null);
  }, []);

  // 获取当前选中的颜色索引
  const getSelectedColorIndex = useCallback((productId: string) => {
    return selectedColors[productId] || 0;
  }, [selectedColors]);

  // 使用 useMemo 优化筛选逻辑
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchTab = activeTab === 'all' || product.brand_id === activeTab;
      const matchSearch = !searchText || 
        product.product_name.toLowerCase().includes(searchText.toLowerCase()) ||
        product.product_code.toLowerCase().includes(searchText.toLowerCase());
      return matchTab && matchSearch;
    });
  }, [products, activeTab, searchText]);

  return (
    <div className="product-list-container">
      {/* 搜索栏 */}
      <SearchHeader 
        placeholder="搜索商品名称或编码" 
        onChange={handleSearch}
        onSearch={handleSearch}
      />

      {/* 标签页 */}
      <div className="tabs-wrapper">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          style={{ '--title-font-size': '15px' }}
        >
          <Tabs.Tab title="全部" key="all" />
          {brands.map(brand => (
            <Tabs.Tab title={brand.brand_name} key={brand.id} />
          ))}
        </Tabs>
      </div>

      {/* 商品列表 */}
      <PullToRefresh onRefresh={loadProducts}>
        {loading ? (
          <ProductSkeleton count={5} />
        ) : filteredProducts.length === 0 ? (
          <EmptyState 
            icon="🛍️" 
            title="暂无商品" 
            description={searchText ? '没有找到相关商品' : '点击右下角按钮添加商品'} 
          />
        ) : (
          <div className="product-list">
            {filteredProducts.map((product, index) => {
              const selectedColorIndex = getSelectedColorIndex(product.id);
              const hasColors = product.product_colors && product.product_colors.length > 0;
              const hasImages = product.product_images && product.product_images.length > 0;
              const currentImage = hasImages && product.product_images 
                ? (product.product_images[selectedColorIndex] || product.product_images[0]) 
                : '';
              
              return (
              <div 
                key={product.id} 
                className="product-item"
                onClick={() => handleProductClick(product.id)}
              >
                {/* 商品图片 */}
                <div 
                  className="product-image-wrapper"
                  onClick={(e) => handleProductDetailClick(product.id, e)}
                >
                  <LazyImage
                    src={currentImage || 'https://via.placeholder.com/160x160/f5f5f5/999999?text=暂无图片'}
                    alt={product.product_name}
                    className="product-image"
                    width={160}
                    height={160}
                    quality={85}
                    priority={index < 3} // 前3个商品优先加载
                  />
                  {hasImages && product.product_images && product.product_images.length > 1 && (
                    <div className="image-count-badge">
                      {product.product_images.length}张
                    </div>
                  )}
                </div>

                {/* 商品信息 */}
                <div className="product-info">
                  {/* 商品名称 */}
                  <div className="product-name">{product.product_name}</div>
                  
                  {/* 商品编码 */}
                  <div className="product-code">编码: {product.product_code}</div>

                  {/* 价格信息 */}
                  <div className="product-price-wrapper">
                    <div className="current-price">
                      <span className="price-symbol">¥</span>
                      <span className="price-value">{product.product_price.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* 颜色选择器 - 下拉框 */}
                  {hasColors && product.product_colors && (
                    <div className="product-color-selector" onClick={(e) => e.stopPropagation()}>
                      <div 
                        className="color-picker-trigger-list"
                        onClick={() => setColorPickerVisible(product.id)}
                      >
                        <span className="color-label">颜色:</span>
                        <span className="color-value">
                          {product.product_colors[selectedColorIndex] || `颜色${selectedColorIndex + 1}`}
                        </span>
                        <DownOutline className="color-arrow" />
                      </div>
                      <Picker
                        columns={[
                          product.product_colors.map((color, index) => ({
                            label: color || `颜色${index + 1}`,
                            value: index,
                          }))
                        ]}
                        visible={colorPickerVisible === product.id}
                        onClose={() => setColorPickerVisible(null)}
                        value={[selectedColorIndex]}
                        onConfirm={(value) => handleColorSelect(product.id, value)}
                      />
                    </div>
                  )}

                  {/* 品牌信息 */}
                  <div className="product-brand">
                    {product.brand?.brand_name || '未知品牌'}
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </PullToRefresh>

      {/* 底部导航 */}
      <BottomNav />

      {/* 添加按钮 */}
      <FloatingBubble
        style={{
          '--initial-position-bottom': '80px',
          '--initial-position-right': '24px',
          '--edge-distance': '24px',
          '--background': '#ff6700',
        }}
        onClick={handleAddProduct}
      >
        <AddOutline fontSize={32} />
      </FloatingBubble>
    </div>
  );
};

export default ProductList;
